import { computed, runInAction, action, observable, makeObservable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import SessionData from './SessionData';
import ApiService from '../Api/ApiService';
import ApiConfiguration from '../Api/ApiConfiguration';
import LoggerFactory from '../../Util/Logger/LoggerFactory';
import ConfigModel from '../Config/ConfigModel';
import SessionPersistence from '../Persistence/SessionPersistence';
import AuthService from '../Auth/AuthService';
import defaultConfig from '../Config/defaultConfig';
import { removeTrailingSlashes } from '../../Util/url';
import SessionStoragePersistence from '../Persistence/SessionStoragePersistence';
import { ApiBaseData, ConfigurationParameters, FetchAPI, HTTPHeaders } from '@ibe/api';
import { getCookies } from '../../Util/cookies';

// ConfigService == everything "static", will not change after app has booted
// AppService    == everything else, which has to be "dynamic"

class AppService {
  private persistence: SessionPersistence<SessionData>;

  private tracePersistence: SessionStoragePersistence<string>;

  private logger = LoggerFactory.get('AppService');

  private configuration: ConfigModel = defaultConfig;

  private currencyValue: string | null = null;

  private language: string | null = null;

  private tenantCode: string | null = null;

  private affiliateNumber: string | null = null;

  private _apiHeaders: HTTPHeaders = {};

  private _correlationId = '';

  currencies: ApiBaseData[] = [];

  constructor(
    private authService: AuthService,
    private fetch?: FetchAPI,
    private externalApiConfig?: Partial<ConfigurationParameters>
  ) {
    makeObservable<
      AppService,
      'configuration' | 'currencyValue' | 'language' | 'tenant' | '_apiHeaders'
    >(this, {
      configuration: observable,
      currencyValue: observable,
      language: observable,
      api: computed,
      currency: computed,
      lang: computed,
      tenant: computed,
      affiliate: computed,
      apiHeaders: computed,
      _apiHeaders: observable,
      setCurrency: action,
      setLang: action,
      setTenant: action,
      setAffiliate: action,
      setApiHeaders: action,
      config: computed,
      hydrate: action,
      persist: action,
      setConfigOnlyForStorybook: action,
      currencies: observable
    });
  }

  get api(): ApiService {
    return new ApiService(
      new ApiConfiguration({
        basePath: removeTrailingSlashes(this.configuration.apiUrl),
        fetchApi: this.fetch,
        apiKey: this.authService.state.token || undefined,
        headers: {
          'Accept-Language': this.lang,
          'X-Correlation-Id': this.correlationId,
          Currency: this.currency,
          Tenant: this.tenant || '',
          Affiliate: this.affiliate || '',
          ...this._apiHeaders
        },
        ...this.externalApiConfig
      })
    );
  }

  get currency(): string {
    return this.currencyValue || this.configuration.currency || this.configuration.defaultCurrency;
  }

  get lang(): string {
    return this.language || this.configuration.defaultLanguage;
  }

  setCurrency(c: string): Promise<void> {
    this.logger.log(`set currency: ${c}`);
    this.currencyValue = c;
    return this.persist();
  }

  setTenant(c: string): Promise<void> {
    this.logger.log(`set tenant: ${c}`);
    this.tenantCode = c;
    return this.persist();
  }

  get tenant(): string | null {
    return this.tenantCode;
  }

  setAffiliate(a: string): Promise<void> {
    this.logger.log(`set affiliateNumber: ${a}`);
    this.affiliateNumber = a;
    return this.persist();
  }

  get affiliate(): string | null {
    return this.affiliateNumber;
  }

  setCorrelationId(correlationId: string): void {
    this._correlationId = correlationId;
  }

  get correlationId(): string {
    return this._correlationId;
  }

  setApiHeaders(headers: HTTPHeaders): void {
    this._apiHeaders = headers;
  }

  get apiHeaders(): HTTPHeaders {
    return this._apiHeaders;
  }

  setLang(l: string): void {
    this.language = l;
  }

  get config(): ConfigModel {
    return this.configuration;
  }

  async hydrate(config: ConfigModel, isServerSide?: boolean): Promise<void> {
    this.configuration = config;
    let hasData;
    let hasTraceData;
    let traceData = '';

    if (!isServerSide) {
      this.persistence = new SessionPersistence<SessionData>(SessionData);
      this.persistence.useKey(this.configuration.sessionKeyApp);
      hasData = this.persistence.exists();
      if (this.configuration.useTid) {
        this.tracePersistence = new SessionStoragePersistence<string>('');
        this.tracePersistence.useKey('ibetid');
        hasTraceData = this.tracePersistence.exists();
        traceData = hasTraceData ? this.tracePersistence.get() : uuidv4();
      } else {
        const cookies = getCookies();
        const correlationId = cookies.correlationId;
        if (correlationId) {
          traceData = correlationId;
        }
      }

      if (this.configuration.useTid) {
        this.tracePersistence.set(traceData);
      }
    }

    const data = hasData ? await this.persistence.get() : new SessionData();

    runInAction(() => {
      this.currencyValue = data.currency || this.currency;
      this.tenantCode = data.tenant || this.configuration.tenant;
      this.affiliateNumber = data.affiliate || this.configuration.affiliate;
      this._correlationId = traceData;
    });
  }

  async persist(): Promise<void> {
    if (this.persistence) {
      this.persistence.set({
        currency: this.currency,
        tenant: this.tenant,
        affiliate: this.affiliate
      });
    }
  }

  setConfigOnlyForStorybook(config: ConfigModel): void {
    // config should not change in real app during runtime,
    // but in storybook we want to mock it per story
    this.configuration = config;
  }

  async loadCurrencies(): Promise<void> {
    let currencies: ApiBaseData[] = [];
    try {
      currencies = await this.api.getAllCurrencies();
    } catch (e) {
      this.logger.warn(e);
    }
    runInAction(() => {
      this.currencies = currencies;
    });
  }
}

export default AppService;
